import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './CourseCalendar.css';
import Sidebar from '../../components/Sidebar';
import SvgIcon from '../../components/SvgIcon';
import NotificationCentre from '../../components/NotificationCentre/NotificationCentre';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}
function toDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
function parseDateStr(str) {
  const [y, m, d] = str.split('-').map(Number);
  return { year: y, month: m - 1, day: d };
}
function formatDisplayDate(dateStr) {
  const { year, month, day } = parseDateStr(dateStr);
  const d = new Date(year, month, day);
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
function formatToDMY(dateStr) {
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

function capitalizeWords(str) {
  if (!str) return '';
  return str.replace(/\b\w/g, c => c.toUpperCase());
}

// Timezone safe range helper
function getDatesInRange(startDateStr, endDateStr) {
  const dates = [];
  const [sy, sm, sd] = startDateStr.split('-').map(Number);
  const [ey, em, ed] = endDateStr.split('-').map(Number);

  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const cur = new Date(start);

  while (cur <= end) {
    dates.push(toDateStr(cur.getFullYear(), cur.getMonth(), cur.getDate()));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

// Generate the 5 or 6 weeks array of dates (strings) for the monthly grid
function getCalendarWeeks(year, month) {
  const firstDay = new Date(year, month, 1);
  const startOffset = firstDay.getDay(); // 0: Sun, 1: Mon...

  const gridStartDate = new Date(firstDay);
  gridStartDate.setDate(firstDay.getDate() - startOffset);

  const weeks = [];
  const currentDate = new Date(gridStartDate);

  for (let w = 0; w < 6; w++) {
    const weekDates = [];
    for (let d = 0; d < 7; d++) {
      weekDates.push(toDateStr(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    weeks.push(weekDates);
  }

  // Drop the 6th week if it doesn't contain any days of the current month
  const lastWeekHasCurrentMonth = weeks[5].some(d => {
    const { month: m } = parseDateStr(d);
    return m === month;
  });
  if (!lastWeekHasCurrentMonth) {
    weeks.pop();
  }

  return weeks;
}

// ─── Holidays ────────────────────────────────────────────────────────────────
const HOLIDAYS = [
  {
    name: 'PUBLIC HOLIDAY',
    startDate: '2026-06-01',
    endDate: '2026-06-02',
    category: 'Public Holiday',
    accentColor: '#10b981',
    startTime: '09:00 AM',
    endTime: '05:00 PM',
  },
  {
    name: 'PUBLIC HOLIDAY',
    startDate: '2026-06-17',
    endDate: '2026-06-17',
    category: 'Public Holiday',
    accentColor: '#10b981',
    startTime: '09:00 AM',
    endTime: '05:00 PM',
  }
];

// ─── Component ────────────────────────────────────────────────────────────────
const CourseCalendar = ({ user, onLogout, onSwitchView }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [screen, setScreen] = useState('calendar'); // 'calendar' | 'detail'
  const isLearner = !user?.role || user.role === 'Learner' || (user.role !== 'Admin' && user.role !== 'Editor' && user.role !== 'Trainer');
  const [viewMode, setViewMode] = useState(isLearner ? 'cards' : 'calendar'); // 'calendar' | 'cards'

  // Ensure learner role defaults to card view even if user prop resolves after initial render
  const hasInitializedViewMode = useRef(false);
  useEffect(() => {
    if (!hasInitializedViewMode.current && user) {
      hasInitializedViewMode.current = true;
      const isLearnerRole = !user.role || user.role === 'Learner' || (user.role !== 'Admin' && user.role !== 'Editor' && user.role !== 'Trainer');
      if (isLearnerRole) {
        setViewMode('cards');
      }
    }
  }, [user]);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter checkboxes
  const [activeFilters, setActiveFilters] = useState({
    closed: true,
    publicTraining: true,
    publicHoliday: true,
    registeredApproved: true
  });

  const [onlyMyClasses, setOnlyMyClasses] = useState(user && user.role === 'Trainer');

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [hoveredCourse, setHoveredCourse] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Detail screen state
  const [detailMonth, setDetailMonth] = useState(today.getMonth());
  const [detailYear, setDetailYear] = useState(today.getFullYear());
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [registering, setRegistering] = useState(false);

  const [registrations, setRegistrations] = useState([]);
  const [clashError, setClashError] = useState(null);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(null);
  const [showWithdrawSuccess, setShowWithdrawSuccess] = useState(false);

  // Special-Project Access Restriction & Deep Link Guard
  const [unlockedProjectCode, setUnlockedProjectCode] = useState(null);
  const [accessDeniedModal, setAccessDeniedModal] = useState(null); // { isOpen, courseName, courseId, error, loading }
  const [modalProjectCode, setModalProjectCode] = useState('');

  // Blackout and Re-Enrollment policy states
  const [learnerBlackout, setLearnerBlackout] = useState(null);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [blackoutAlertModal, setBlackoutAlertModal] = useState(null);
  const [successNotice, setSuccessNotice] = useState(null);

  // Check active blackout status for learner on mount
  useEffect(() => {
    if (user?.email) {
      fetch(`/api/learner/blackout-status/${encodeURIComponent(user.email)}`)
        .then(res => res.json())
        .then(data => {
          if (data.status === 'success' && data.data?.isBlacklisted) {
            setLearnerBlackout(data.data);
          } else {
            setLearnerBlackout(null);
          }
        })
        .catch(err => console.error('Error checking blackout status:', err));
    }
  }, [user?.email]);

  // Learner Self Check-In state
  const [selfCheckInOpen, setSelfCheckInOpen] = useState(false);
  const [selfCheckInCode, setSelfCheckInCode] = useState('');
  const [selfCheckInSubmitting, setSelfCheckInSubmitting] = useState(false);
  const [selfCheckInMsg, setSelfCheckInMsg] = useState(null);

  const handleSelfCheckInSubmit = async (e) => {
    e.preventDefault();
    if (!selfCheckInCode.trim()) return;
    setSelfCheckInSubmitting(true);
    setSelfCheckInMsg(null);
    try {
      const res = await fetch('/api/learner/attendance/self-checkin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          learnerEmail: user.email,
          sessionCode: selfCheckInCode.trim()
        })
      });
      const data = await res.json();
      if (res.ok) {
        setSelfCheckInMsg({ type: 'success', text: data.message });
        setSelfCheckInCode('');
        fetchRegistrations();
      } else {
        setSelfCheckInMsg({ type: 'error', text: data.message || 'Check-in failed.' });
      }
    } catch (err) {
      setSelfCheckInMsg({ type: 'error', text: 'Connection error. Failed to process check-in.' });
    } finally {
      setSelfCheckInSubmitting(false);
    }
  };

  // Fetch active registrations for the logged-in learner
  const fetchRegistrations = () => {
    if (!user || !user.email) return;
    fetch(`/api/learner/registrations/${encodeURIComponent(user.email)}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch registrations');
        return res.json();
      })
      .then(res => {
        if (res.status === 'success') {
          setRegistrations(res.data);
        }
      })
      .catch(err => {
        console.error('Error fetching registrations:', err);
      });
  };

  useEffect(() => {
    fetchRegistrations();
  }, [user]);

  // Helper function to convert time string (e.g. "09:00 AM") to minutes from midnight
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const match = timeStr.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
    if (!match) return 0;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const ampm = match[3].toUpperCase();
    if (ampm === 'PM' && hours < 12) hours += 12;
    if (ampm === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  // Helper to check if a selected session clashes with existing bookings
  const checkClash = (session) => {
    if (!session || !registrations.length) return null;

    const scheduleId = parseInt(session.id.replace('course-', ''), 10);
    const targetSegments = (session.sessionSegments && Array.isArray(session.sessionSegments) && session.sessionSegments.length > 0)
      ? session.sessionSegments
      : [{ startDate: session.startDate, endDate: session.endDate, startTime: session.startTime, endTime: session.endTime }];

    for (const reg of registrations) {
      if (reg.Status === 'Cancelled' || reg.Status === 'Withdrawn' || reg.Status === 'Rejected') {
        continue;
      }
      if (parseInt(reg.ScheduleID) === scheduleId) {
        return `You are already registered for this course schedule.`;
      }

      let regSegments = null;
      if (reg.SessionSegments) {
        try {
          regSegments = typeof reg.SessionSegments === 'string' ? JSON.parse(reg.SessionSegments) : reg.SessionSegments;
        } catch (e) {
          regSegments = null;
        }
      }
      if (!Array.isArray(regSegments) || regSegments.length === 0) {
        regSegments = [{ startDate: reg.StartDate, endDate: reg.EndDate, startTime: reg.StartTime, endTime: reg.EndTime }];
      }

      for (const tSeg of targetSegments) {
        const tStartMinutes = parseTimeToMinutes(tSeg.startTime || session.startTime);
        const tEndMinutes = parseTimeToMinutes(tSeg.endTime || session.endTime);

        for (const rSeg of regSegments) {
          // Check date overlap: (startA <= endB && endA >= startB)
          const dateOverlap = (tSeg.startDate <= rSeg.endDate && tSeg.endDate >= rSeg.startDate);

          if (dateOverlap) {
            // Check daily time overlap: (startTimeA < endTimeB && endTimeA > startTimeB)
            const regStartMinutes = parseTimeToMinutes(rSeg.startTime || reg.StartTime);
            const regEndMinutes = parseTimeToMinutes(rSeg.endTime || reg.EndTime);

            const timeOverlap = (tStartMinutes < regEndMinutes && tEndMinutes > regStartMinutes);

            if (timeOverlap) {
              return reg.CourseName;
            }
          }
        }
      }
    }
    return null;
  };

  // Fetch course schedules from DB & map
  useEffect(() => {
    let url = '/api/schedules';
    const params = new URLSearchParams();
    if (user?.email) params.append('email', user.email);
    if (user?.role) params.append('role', user.role);
    if (unlockedProjectCode) params.append('projectCode', unlockedProjectCode);
    if (params.toString()) url += `?${params.toString()}`;

    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch schedules');
        return res.json();
      })
      .then(res => {
        if (res.status === 'success') {
          const mappedEvents = [];

          // 1. Add holidays
          HOLIDAYS.forEach((item, index) => {
            const dates = getDatesInRange(item.startDate, item.endDate);
            mappedEvents.push({
              id: `holiday-${index}`,
              name: item.name,
              shortName: item.name,
              category: item.category,
              accentColor: item.accentColor,
              startDate: item.startDate,
              endDate: item.endDate,
              startTime: item.startTime,
              endTime: item.endTime,
              dates,
              dbCourse: null,
              venue: 'N/A'
            });
          });

          // 2. Map database course schedules, filtering out non-training types (e.g. Exam only)
          res.data
            .filter(schedule => (schedule.Type || '').toLowerCase().includes('training'))
            .forEach((schedule, index) => {
              const dbCourse = {
                CourseID: schedule.CourseID,
                CertificationBody: schedule.CertificationBody,
                CourseName: schedule.CourseName,
                Type: schedule.Type,
                Description: schedule.Description
              };

              const type = (schedule.Type || '').toLowerCase().trim();
              const isExamOnly = type.includes('exam') && !type.includes('training');

              const startDate = schedule.StartDate;
              const endDate = schedule.EndDate;

              let parsedSegments = null;
              if (schedule.SessionSegments) {
                try {
                  parsedSegments = typeof schedule.SessionSegments === 'string' ? JSON.parse(schedule.SessionSegments) : schedule.SessionSegments;
                } catch (e) {
                  parsedSegments = null;
                }
              }

              let dates = [];
              if (Array.isArray(parsedSegments) && parsedSegments.length > 0) {
                parsedSegments.forEach(seg => {
                  if (seg.startDate && seg.endDate) {
                    dates = dates.concat(getDatesInRange(seg.startDate, seg.endDate));
                  } else if (seg.startDate) {
                    dates.push(seg.startDate);
                  }
                });
                dates = Array.from(new Set(dates)).sort();
              } else {
                dates = getDatesInRange(startDate, endDate);
              }

              // Start/End Time
              const startTime = schedule.StartTime || '09:00 AM';
              const endTime = schedule.EndTime || '05:00 PM';

              // Determine name
              let name = `${schedule.CertificationBody} ${schedule.CourseName}`;

              // Designate specific indexes as online (e.g. index 3, 7, 11, 15, 19)
              const isOnline = (index === 3 || index === 7 || index === 11 || index === 15 || index === 19);
              if (isOnline) {
                name = `[Online] ${name}`;
              }

              // Category and color
              // Designate index 4 (ScheduleID 5) as the one Closed Event
              let category = 'Public Training Class';
              let accentColor = '#1e3a5f'; // Navy
              if (index === 4) {
                category = 'Closed Event';
                accentColor = '#ef4444'; // Red
              }

              // Venue
              const venue = isOnline ? 'Online' : (isExamOnly ? 'Voucher Delivery' : 'CCoE Training Centre, Cyberjaya');

              const isClosed = schedule.IsClosed === 1;
              const isCancelled = schedule.IsCancelled === 1;
              const cancellationReason = schedule.CancellationReason;
              const enrolledCount = schedule.EnrolledCount || 0;
              const maxCapacity = schedule.MaxCapacity || 10;
              const isFull = enrolledCount >= maxCapacity;

              mappedEvents.push({
                id: `course-${schedule.ScheduleID}`,
                name,
                shortName: name,
                category,
                accentColor,
                startDate,
                endDate,
                startTime,
                endTime,
                sessionSegments: parsedSegments,
                dates,
                dbCourse,
                venue,
                trainerName: schedule.TrainerName,
                isClosed,
                isCancelled,
                cancellationReason,
                isFull,
                enrolledCount,
                maxCapacity,
                assignedTags: schedule.assignedTags || [],
                isRestricted: schedule.isRestricted || false
              });
            });

          setEvents(mappedEvents);
        } else {
          setError(res.message);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError(err.message);
        setLoading(false);
      });
  }, [user, unlockedProjectCode]);

  // Deep Link & Course Access Guard
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const targetCourseId = params.get('courseId');
    const targetScheduleId = params.get('scheduleId');

    if (targetCourseId || targetScheduleId) {
      if (events.length > 0) {
        const found = events.find(e => 
          (targetCourseId && e.dbCourse?.CourseID === parseInt(targetCourseId, 10)) ||
          (targetScheduleId && e.id === `course-${targetScheduleId}`)
        );

        if (found) {
          openCourse(found);
        } else {
          // If course is not in accessible events list, do not probe server with showAll
          setAccessDeniedModal({
            isOpen: true,
            courseName: 'Restricted or Unavailable Course',
            courseId: targetCourseId || targetScheduleId,
            error: 'You do not have access to view this course. Please contact your administrator if you have a project tag.'
          });
        }
      }
    }
  }, [events]);

  const handleUnlockCourse = async (e) => {
    e.preventDefault();
    if (!modalProjectCode.trim()) return;
    setAccessDeniedModal(prev => ({ ...prev, loading: true, error: '' }));

    try {
      const res = await fetch('/api/special-project/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectCode: modalProjectCode.trim() })
      });
      const data = await res.json();
      if (res.ok && data.valid) {
        if (user?.email) {
          fetch('/api/profile/project-code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: user.email, projectCode: modalProjectCode.trim() })
          }).catch(console.error);
        }
        setUnlockedProjectCode(modalProjectCode.trim().toUpperCase());
        setAccessDeniedModal(null);
        setModalProjectCode('');
      } else {
        setAccessDeniedModal(prev => ({ ...prev, loading: false, error: data.message || 'Invalid project code.' }));
      }
    } catch (err) {
      setAccessDeniedModal(prev => ({ ...prev, loading: false, error: 'Connection error. Please try again.' }));
    }
  };


  // Enhance events with registration status & pink color for approved classes
  const enhancedEvents = useMemo(() => {
    return events.map(evt => {
      if (evt.category === 'Public Holiday') return evt;
      const scheduleId = parseInt((evt.id || '').replace('course-', ''), 10);
      const isApproved = isLearner && registrations.some(r =>
        parseInt(r.ScheduleID, 10) === scheduleId &&
        (r.Status === 'Approved' || (r.Status || '').toLowerCase() === 'approved')
      );
      if (isApproved) {
        return {
          ...evt,
          accentColor: '#db2777',
          isRegisteredApproved: true
        };
      }
      return {
        ...evt,
        isRegisteredApproved: false
      };
    });
  }, [events, registrations, isLearner]);

  // Filter events based on active filters
  const filteredEvents = enhancedEvents.filter(e => {
    if (e.isRegisteredApproved) {
      if (!activeFilters.registeredApproved) return false;
      return true;
    }
    if (e.category === 'Closed Event' && !activeFilters.closed) return false;
    if (e.category === 'Public Training Class' && !activeFilters.publicTraining) return false;
    if (e.category === 'Public Holiday' && !activeFilters.publicHoliday) return false;
    if (onlyMyClasses && user?.fullName && e.category !== 'Public Holiday') {
      if (user?.role === 'Trainer') {
        return e.trainerName?.toLowerCase() === user.fullName.toLowerCase();
      }
    }
    return true;
  });

  // Generate calendar weeks array
  const weeks = getCalendarWeeks(viewYear, viewMonth);

  // Navigation controls
  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const openCourse = (eventItem) => {
    if (eventItem.category === 'Public Holiday') return; // Holidays are not clickable

    const dbCourse = eventItem.dbCourse;

    // Parse duration details
    let durationText = '1 Day';
    let attemptsText = '';
    let isTraining = false;
    let isExam = false;

    if (dbCourse) {
      const type = (dbCourse.Type || '').toLowerCase().trim();
      const desc = (dbCourse.Description || '').toLowerCase();

      isTraining = type.includes('training') || type.includes('promo');
      isExam = type.includes('exam');
      const isExamOnly = type.includes('exam') && !type.includes('training');

      if (isTraining) {
        // Match combo pattern first e.g. "4 days training + 1 day exam"
        const comboMatch = dbCourse.Description.match(/(\d+\s*days?\s*training\s*\+\s*\d+\s*day\s*exam)/i);
        const daysMatch = dbCourse.Description.match(/(\d+\s*days?)/i);
        const hoursMatch = dbCourse.Description.match(/(\d+\s*hours?)/i);
        if (comboMatch) durationText = comboMatch[1];
        else if (daysMatch) durationText = daysMatch[1];
        else if (hoursMatch) durationText = hoursMatch[1];
      } else if (isExamOnly) {
        durationText = '-';
      }

      // Extract attempts
      const attemptsMatch = dbCourse.Description.match(/(\d+\s*attempts?)/i);
      if (attemptsMatch) {
        attemptsText = `Exam voucher (${attemptsMatch[1]})`;
      } else if (desc.includes('voucher')) {
        attemptsText = 'Exam voucher included';
      }
    } else {
      durationText = `${eventItem.dates.length} Days`;
    }

    if (eventItem.sessionSegments && eventItem.sessionSegments.length > 1) {
      durationText = `${eventItem.dates?.length || 0} Days (${eventItem.sessionSegments.length} Parts)`;
    }

    // Find all scheduled dates for this course in the current schedule list
    const relatedSessions = enhancedEvents.filter(e =>
      (e.dbCourse && dbCourse)
        ? e.dbCourse.CourseID === dbCourse.CourseID
        : e.name === eventItem.name
    );

    const firstSession = relatedSessions[0];
    if (firstSession) {
      const { year, month } = parseDateStr(firstSession.startDate);
      setDetailYear(year);
      setDetailMonth(month);
    }

    setSelectedCourse({
      event: eventItem,
      dbCourse,
      name: dbCourse ? `${dbCourse.CertificationBody} ${dbCourse.CourseName} (${dbCourse.Type})` : eventItem.name,
      category: eventItem.category,
      accentColor: eventItem.accentColor,
      description: dbCourse ? dbCourse.Description : 'No course description available.',
      fee: dbCourse ? (dbCourse.Type.toLowerCase() === 'exam' ? 'RM 1,200' : `RM ${2000 + (dbCourse.CourseID % 4) * 500}`) : 'RM 2,500',
      trainer: eventItem.trainerName || (dbCourse ? (dbCourse.Type.toLowerCase() === 'exam' ? 'N/A' : ['Mr. Ahmad Zulkifli', 'Ms. Nurul Ain Hassan', 'Mr. Rizal Ismail', 'Ms. Siti Rohani Abdullah'][dbCourse.CourseID % 4]) : 'Mr. Rizal Ismail'),
      duration: capitalizeWords(durationText),
      venue: eventItem.venue,
      isTraining,
      isExam,
      attemptsText,
      sessions: relatedSessions
    });

    setSelectedSession(null);
    setSelectedTimeSlot(null);
    setEligibilityData(null);
    setClashError(null);
    setScreen('detail');
    window.scrollTo(0, 0);
  };

  const evaluateEligibility = useCallback(async (session) => {
    if (!user?.email || !session) return;
    setCheckingEligibility(true);
    setEligibilityData(null);
    try {
      const scheduleId = session.id.replace('course-', '');
      const res = await fetch(`/api/learner/enrollment-eligibility?email=${encodeURIComponent(user.email)}&scheduleId=${scheduleId}`);
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setEligibilityData(data.data);
        if (data.data.status === 'blackout') {
          setClashError(data.data.message);
          setLearnerBlackout({
            isBlacklisted: true,
            blackout: { Reason: data.data.blackoutReason, EndDate: data.data.blackoutEndDate },
            daysRemaining: data.data.daysRemaining
          });
        }
      }
    } catch (err) {
      console.error('Error evaluating enrollment eligibility:', err);
    } finally {
      setCheckingEligibility(false);
    }
  }, [user?.email]);

  const handleRegister = () => {
    if (!selectedSession || !selectedTimeSlot || clashError || learnerBlackout?.isBlacklisted) return;
    setRegistering(true);

    const scheduleId = selectedSession.id.replace('course-', '');

    fetch('/api/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: user.email,
        scheduleId: scheduleId
      })
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          if (data.status === 'blackout') {
            setBlackoutAlertModal({
              isOpen: true,
              message: data.message,
              daysRemaining: data.daysRemaining,
              reason: data.reason,
              endDate: data.blackoutEndDate
            });
            setLearnerBlackout({
              isBlacklisted: true,
              blackout: { Reason: data.reason, EndDate: data.blackoutEndDate },
              daysRemaining: data.daysRemaining
            });
          }
          throw new Error(data.message || 'Registration failed.');
        }
        return data;
      })
      .then((data) => {
        setRegistering(false);
        setSuccessNotice(data.policyNotice || null);
        setShowSuccess(true);
        fetchRegistrations();
      })
      .catch((err) => {
        setRegistering(false);
        setClashError(err.message);
      });
  };

  const handleSuccessDone = () => {
    setShowSuccess(false);
    setSuccessNotice(null);
    setSelectedSession(null);
    setSelectedTimeSlot(null);
    setEligibilityData(null);
    setClashError(null);
    setScreen('calendar');
  };

  const handleWithdraw = (registrationId) => {
    setShowWithdrawConfirm(registrationId);
  };

  const executeWithdraw = (registrationId) => {
    fetch(`/api/learner/registrations/${registrationId}/withdraw`, {
      method: 'PUT'
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to withdraw');
        return res.json();
      })
      .then(data => {
        if (data.status === 'success') {
          setShowWithdrawSuccess(true);
          fetchRegistrations();
        } else {
          alert(data.message || "Failed to withdraw.");
        }
      })
      .catch(err => {
        console.error('Error during withdrawal:', err);
        alert("Failed to connect to the server.");
      });
  };

  // Mini-calendar helpers
  const detailDaysInMonth = getDaysInMonth(detailYear, detailMonth);
  const detailFirstDay = getFirstDayOfMonth(detailYear, detailMonth);

  const courseDateMap = {};
  if (selectedCourse) {
    selectedCourse.sessions.forEach(session => {
      session.dates.forEach(date => {
        courseDateMap[date] = session;
      });
    });
  }

  const prevDetailMonth = () => {
    if (detailMonth === 0) { setDetailMonth(11); setDetailYear(y => y - 1); }
    else setDetailMonth(m => m - 1);
  };
  const nextDetailMonth = () => {
    if (detailMonth === 11) { setDetailMonth(0); setDetailYear(y => y + 1); }
    else setDetailMonth(m => m + 1);
  };

  if (!user) {
    return (
      <div className="profile-loading">
        <p>Please log in to view the course calendar.</p>
      </div>
    );
  }

  // ── Render ──
  return (
    <div className="profile-page-layout">
      <Sidebar activeView="calendar" onSwitchView={onSwitchView} onLogout={onLogout} user={user} />

      {/* Main content */}
      <div className="profile-main-content">
        {/* Top bar */}
        <header className="profile-top-bar cc-top-bar">
          <div className="profile-top-bar-left">
            <SvgIcon name="calendar" size={22} color="#4f46e5" />
            <h1>Course Calendar</h1>
          </div>
          <div className="profile-top-bar-right">
            {user && (
                <div 
                  className="profile-top-bar-user" 
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                  onClick={() => {
                    if (onSwitchView) onSwitchView('account');
                  }}
                  title="View Profile Settings"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (onSwitchView) onSwitchView('account');
                    }
                  }}
                >
                  <div className="profile-top-bar-empty-avatar">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                  </div>
                  <span 
                    className="profile-top-bar-username"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSwitchView) onSwitchView('account');
                    }}
                  >
                    {user.fullName || user.name || user.username || 'User'}
                  </span>
                </div>
            )}
          </div>
        </header>

        {loading ? (
          <div className="profile-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', fontSize: '1.2rem', color: '#555' }}>
            <p>Loading course catalogue and schedules...</p>
          </div>
        ) : error ? (
          <div className="profile-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', fontSize: '1.2rem', color: '#ef4444' }}>
            <p>Error: {error}</p>
          </div>
        ) : (
          <>

        {/* ── SCREEN: Calendar ── */}
        {screen === 'calendar' && (
          <div className="cc-page-body cc-centered-calendar-view">

            {/* In-App Notification Centre */}
            <NotificationCentre user={user} />

            {/* Calendar header controls */}
            <div className="cc-calendar-header">
              <div className="cc-calendar-header-left">
                <h2 className="cc-calendar-title">
                  {MONTH_NAMES[viewMonth]} {viewYear}
                </h2>
                <span className="cc-calendar-subtitle">Training Schedule and Holidays</span>
              </div>
              <div className="cc-calendar-header-right">
                <div className="cc-view-toggle-bar">
                  <button
                    className={`cc-view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
                    onClick={() => setViewMode('cards')}
                  >
                    Card View
                  </button>
                  <button
                    className={`cc-view-toggle-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                    onClick={() => setViewMode('calendar')}
                  >
                    Calendar View
                  </button>
                </div>
                {viewMode === 'calendar' && (
                  <div className="cc-month-nav-group">
                    <button className="cc-nav-btn" onClick={prevMonth} aria-label="Previous month" style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                      &lt;
                    </button>
                    <button
                      className="cc-today-btn"
                      onClick={() => { setViewMonth(today.getMonth()); setViewYear(today.getFullYear()); }}
                    >
                      Today
                    </button>
                    <button className="cc-nav-btn" onClick={nextMonth} aria-label="Next month" style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                      &gt;
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* HORIZONTAL CATEGORIES FILTER BAR ON TOP OF CALENDAR */}
            <div className="cc-categories-filter-bar">
              <div className="cc-filter-category-section">
                <span className="cc-filter-title">Category:</span>
                <div className="cc-filter-items-wrap">
                  <label className="cc-filter-item">
                    <input
                      type="checkbox"
                      checked={activeFilters.closed}
                      onChange={(e) => setActiveFilters(prev => ({ ...prev, closed: e.target.checked }))}
                    />
                    <span className="cc-filter-indicator" style={{ background: '#ef4444' }} />
                    <span className="cc-filter-label-text">Closed Event</span>
                  </label>

                  <label className="cc-filter-item">
                    <input
                      type="checkbox"
                      checked={activeFilters.publicTraining}
                      onChange={(e) => setActiveFilters(prev => ({ ...prev, publicTraining: e.target.checked }))}
                    />
                    <span className="cc-filter-indicator" style={{ background: '#1e3a5f' }} />
                    <span className="cc-filter-label-text">Public Training Class</span>
                  </label>

                  <label className="cc-filter-item">
                    <input
                      type="checkbox"
                      checked={activeFilters.publicHoliday}
                      onChange={(e) => setActiveFilters(prev => ({ ...prev, publicHoliday: e.target.checked }))}
                    />
                    <span className="cc-filter-indicator" style={{ background: '#10b981' }} />
                    <span className="cc-filter-label-text">Public Holiday</span>
                  </label>

                  {isLearner && (
                    <label className="cc-filter-item">
                      <input
                        type="checkbox"
                        checked={activeFilters.registeredApproved}
                        onChange={(e) => setActiveFilters(prev => ({ ...prev, registeredApproved: e.target.checked }))}
                      />
                      <span className="cc-filter-indicator" style={{ background: '#db2777' }} />
                      <span className="cc-filter-label-text" style={{ color: '#db2777', fontWeight: 600 }}>
                        Registered Class (Approved)
                      </span>
                    </label>
                  )}
                </div>
              </div>

              {user?.role === 'Trainer' && (
                <div className="cc-filter-trainer-section">
                  <label className="cc-filter-item cc-filter-trainer-item">
                    <input
                      type="checkbox"
                      checked={onlyMyClasses}
                      onChange={(e) => setOnlyMyClasses(e.target.checked)}
                      style={{ accentColor: '#4f46e5' }}
                    />
                    <span className="cc-filter-label-text" style={{ color: '#4f46e5' }}>Only show my classes</span>
                  </label>
                </div>
              )}


            </div>

            {/* Calendar grid or Card view */}
            {viewMode === 'calendar' ? (
              <div className="cc-calendar-outer">
                <div className="cc-mobile-scroll-hint">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="7 13 12 18 17 13"></polyline>
                    <polyline points="7 6 12 11 17 6"></polyline>
                  </svg>
                  <span>Swipe horizontally to view full week</span>
                </div>
                <div className="cc-calendar-scroll-wrap">
                  <div className="cc-calendar-wrap">
                    {/* Day headers */}
                    <div className="cc-day-headers">
                      {DAY_NAMES.map(d => (
                        <div key={d} className="cc-day-header">{d}</div>
                      ))}
                    </div>

                    {/* Weeks Rendering */}
                    <div className="cc-weeks-container">
                      {weeks.map((weekDates, wIdx) => {

                        // Gather segments overlapping this week
                        const weekSegments = [];
                        filteredEvents.forEach(evt => {
                          const datesInWeek = evt.dates.filter(d => weekDates.includes(d));
                          if (datesInWeek.length === 0) return;

                          const colIndices = datesInWeek.map(d => weekDates.indexOf(d));
                          const colStart = Math.min(...colIndices);
                          const colEnd = Math.max(...colIndices);
                          const span = colEnd - colStart + 1;

                          weekSegments.push({
                            event: evt,
                            colStart,
                            colEnd,
                            span
                          });
                        });

                        // Sort segments (longer span first) for nice vertical packing
                        weekSegments.sort((a, b) => b.span - a.span || a.colStart - b.colStart);

                        // Assign row indices (greedy packing)
                        const rowOccupied = [];
                        weekSegments.forEach(seg => {
                          let row = 0;
                          while (true) {
                            if (!rowOccupied[row]) rowOccupied[row] = Array(7).fill(false);

                            let free = true;
                            for (let c = seg.colStart; c <= seg.colEnd; c++) {
                              if (rowOccupied[row][c]) {
                                free = false;
                                break;
                              }
                            }

                            if (free) {
                              for (let c = seg.colStart; c <= seg.colEnd; c++) {
                                rowOccupied[row][c] = true;
                              }
                              seg.row = row;
                              break;
                            }
                            row++;
                          }
                        });

                        const numRows = rowOccupied.length || 1;

                        return (
                          <div key={wIdx} className="cc-week-row" style={{ minHeight: `${80 + numRows * 26}px` }}>

                            {/* Day Cells Background */}
                            <div className="cc-week-bg">
                              {weekDates.map((dateStr, dIdx) => {
                                const { month, day } = parseDateStr(dateStr);
                                const isCurrentMonth = month === viewMonth;
                                const isToday =
                                  day === today.getDate() &&
                                  month === today.getMonth() &&
                                  viewYear === today.getFullYear();

                                return (
                                  <div
                                    key={dIdx}
                                    className={`cc-cell-bg ${isCurrentMonth ? '' : 'cc-cell-other-month'} ${isToday ? 'cc-cell-today' : ''}`}
                                  >
                                    <span className={`cc-day-num ${isToday ? 'cc-day-num-today' : ''}`}>
                                      {day}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Spanning Event Bars Overlay */}
                            <div className="cc-week-events-grid" style={{ gridTemplateRows: `repeat(${numRows}, auto)` }}>
                              {weekSegments.map((seg, sIdx) => {
                                const isHoliday = seg.event.category === 'Public Holiday';
                                const isSingleDay = seg.event.startDate === seg.event.endDate;
                                const hasTimePrefix = isSingleDay && seg.event.startTime && !isHoliday;
                                const displayLabel = hasTimePrefix
                                  ? `${seg.event.startTime.toLowerCase()} ${seg.event.name}`
                                  : seg.event.name;

                                const isApproved = seg.event.isRegisteredApproved;

                                return (
                                  <div
                                    key={sIdx}
                                    className={`cc-event-bar ${isHoliday ? 'cc-event-bar-holiday' : ''} ${isApproved ? 'cc-event-bar-approved' : ''}`}
                                    style={{
                                      gridColumn: `${seg.colStart + 1} / ${seg.colEnd + 2}`,
                                      gridRow: `${seg.row + 1}`,
                                      borderLeftColor: seg.event.accentColor,
                                      background: isApproved ? '#fdf2f8' : `${seg.event.accentColor}18`,
                                      color: seg.event.accentColor,
                                      fontWeight: isApproved ? 600 : undefined
                                    }}
                                    onClick={isHoliday ? undefined : () => openCourse(seg.event)}
                                    onMouseEnter={isHoliday ? undefined : (e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setHoveredCourse({ course: seg.event, session: seg.event });
                                      setTooltipPos({ x: rect.left, y: rect.bottom + 6 });
                                    }}
                                    onMouseLeave={isHoliday ? undefined : () => setHoveredCourse(null)}
                                  >
                                    <span className="cc-event-dot" style={{ background: seg.event.accentColor }} />
                                    <span className="cc-event-name" style={{ color: seg.event.accentColor }}>
                                      {isApproved ? `✓ ${displayLabel}` : displayLabel}
                                    </span>
                              </div>
                            );
                          })}
                        </div>

                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : (
              <div className="cc-card-grid">
                {filteredEvents.filter(e => e.category !== 'Public Holiday').length === 0 ? (
                  <div className="cc-empty-card-state">
                    <p>No courses match your selected filter criteria.</p>
                  </div>
                ) : (
                  filteredEvents.filter(e => e.category !== 'Public Holiday').map((event) => {
                    const scheduleId = parseInt(event.id.replace('course-', ''), 10);
                    const isEnrolled = registrations.some(r => parseInt(r.ScheduleID) === scheduleId);

                    const isApproved = event.isRegisteredApproved || (isLearner && registrations.some(r => 
                      parseInt(r.ScheduleID, 10) === scheduleId && 
                      (r.Status === 'Approved' || (r.Status || '').toLowerCase() === 'approved')
                    ));
                    const enrolledReg = registrations.find(r => parseInt(r.ScheduleID, 10) === scheduleId);

                    return (
                      <div 
                        key={event.id} 
                        className={`cc-course-card ${isApproved ? 'cc-course-card-approved' : ''}`}
                        style={isApproved ? { borderColor: '#fbcfe8', boxShadow: '0 4px 12px rgba(219, 39, 119, 0.08)' } : {}}
                      >
                        <div className="cc-card-accent-bar" style={{ background: event.accentColor }} />
                        <div className="cc-card-body">
                          <div className="cc-card-top-row">
                            <span className="cc-card-category-badge" style={{ background: `${event.accentColor}15`, color: event.accentColor, border: `1px solid ${event.accentColor}30` }}>
                              {event.category}
                            </span>
                            {isApproved ? (
                              <span className="cc-card-enrolled-badge" style={{ background: '#fdf2f8', color: '#db2777', borderColor: '#fbcfe8', fontWeight: 700 }}>
                                ✓ Registered (Approved)
                              </span>
                            ) : isEnrolled ? (
                              <span className="cc-card-enrolled-badge">
                                ✓ Enrolled {enrolledReg?.Status ? `(${enrolledReg.Status})` : ''}
                              </span>
                            ) : null}
                          </div>

                          <h3 className="cc-card-title">{event.name}</h3>

                          {event.dbCourse?.Description && (
                            <div
                              className="cc-card-description"
                              dangerouslySetInnerHTML={{ __html: event.dbCourse.Description }}
                            />
                          )}

                          <div className="cc-card-meta-list">
                            {event.sessionSegments && event.sessionSegments.length > 1 ? (
                              <div className="cc-card-meta-item" style={{ alignItems: 'flex-start' }}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '3px', flexShrink: 0 }}>
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                  <line x1="16" y1="2" x2="16" y2="6"></line>
                                  <line x1="8" y1="2" x2="8" y2="6"></line>
                                  <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '100%' }}>
                                  {event.sessionSegments.map((s, idx) => (
                                    <div key={idx} style={{ fontSize: '0.8rem', color: '#1e293b' }}>
                                      <strong style={{ color: '#047857' }}>Part {idx + 1}:</strong> {formatToDMY(s.startDate)} - {formatToDMY(s.endDate)} <span style={{ color: '#64748b', fontSize: '0.75rem' }}>({s.startTime || event.startTime} - {s.endTime || event.endTime})</span>
                                    </div>
                                  ))}
                                  <div style={{ fontSize: '0.75rem', color: '#047857', fontWeight: 600, marginTop: '2px' }}>
                                    Total: {event.dates?.length || 0} Training Days
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="cc-card-meta-item">
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                  </svg>
                                  <span>{formatToDMY(event.startDate)} - {formatToDMY(event.endDate)}</span>
                                </div>

                                <div className="cc-card-meta-item">
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                  </svg>
                                  <span>{event.startTime} - {event.endTime}</span>
                                </div>
                              </>
                            )}

                            <div className="cc-card-meta-item">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                <circle cx="12" cy="10" r="3"></circle>
                              </svg>
                              <span>{event.venue}</span>
                            </div>

                            {event.trainerName && (
                              <div className="cc-card-meta-item">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                  <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                                <span>Trainer: {event.trainerName}</span>
                              </div>
                            )}
                          </div>

                          <div className="cc-card-footer">
                            <button
                              className={`cc-card-action-btn ${isEnrolled ? 'btn-enrolled' : ''}`}
                              onClick={() => openCourse(event)}
                            >
                              {isEnrolled ? 'View Schedule & Status' : 'View Details & Register →'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Hover tooltip matching the mockup screenshot */}
            {hoveredCourse && (
              <div
                className="cc-tooltip"
                style={{ top: tooltipPos.y, left: Math.min(tooltipPos.x, window.innerWidth - 300) }}
              >
                <div className="cc-tooltip-content-wrapper">
                  <div className="cc-tooltip-title">{hoveredCourse.course.name}</div>

                  <div className="cc-tooltip-info-rows">
                    <div className="cc-tooltip-info-row">
                      <span className="cc-tooltip-icon-container">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                      </span>
                      <span className="cc-tooltip-badge-text" style={{ background: hoveredCourse.course.accentColor }}>
                        {hoveredCourse.course.category}
                      </span>
                    </div>

                    {hoveredCourse.course.sessionSegments && hoveredCourse.course.sessionSegments.length > 1 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(226, 232, 240, 0.8)' }}>
                        {hoveredCourse.course.sessionSegments.map((s, idx) => (
                          <div key={idx} style={{ fontSize: '0.78rem', color: '#1e293b' }}>
                            <strong style={{ color: '#047857' }}>Part {idx + 1}:</strong> {formatToDMY(s.startDate)} - {formatToDMY(s.endDate)}
                            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>({s.startTime || hoveredCourse.course.startTime} - {s.endTime || hoveredCourse.course.endTime})</div>
                          </div>
                        ))}
                        <div style={{ fontSize: '0.72rem', color: '#047857', fontWeight: 700 }}>
                          Total: {hoveredCourse.course.dates?.length || 0} Training Days
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="cc-tooltip-info-row">
                          <span className="cc-tooltip-icon-container">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                          </span>
                          <span className="cc-tooltip-datetime-text">
                            {formatToDMY(hoveredCourse.course.startDate)} {hoveredCourse.course.startTime.toLowerCase()}
                          </span>
                        </div>

                        <div className="cc-tooltip-info-row">
                          <span className="cc-tooltip-icon-container">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                          </span>
                          <span className="cc-tooltip-datetime-text">
                            {formatToDMY(hoveredCourse.course.endDate)} {hoveredCourse.course.endTime.toLowerCase()}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {/* ── SCREEN: Course Detail ── */}
        {screen === 'detail' && selectedCourse && (
          <div className="cc-page-body">
            {/* Back link */}
            <button className="cc-back-btn" onClick={() => { setScreen('calendar'); setHoveredCourse(null); setSelectedSession(null); setSelectedTimeSlot(null); setClashError(null); }} style={{ fontWeight: 'bold' }}>
              Back to Course Calendar
            </button>

            <div className="cc-detail-layout" style={user?.role === 'Admin' ? { gridTemplateColumns: '1fr' } : {}}>
              {/* ── Left: Course Info ── */}
              <div className="cc-detail-left">
                {/* Course Card */}
                <div className="cc-course-card">
                  <div className="cc-course-card-top">
                    <div>
                      <h2 className="cc-course-title">{selectedCourse.name}</h2>
                      <span className="cc-course-cat-badge" style={{ background: `${selectedCourse.accentColor}18`, color: selectedCourse.accentColor, borderColor: `${selectedCourse.accentColor}40` }}>
                        {selectedCourse.category}
                      </span>
                    </div>
                  </div>

                  {/* Meta rows */}
                  <div className="cc-meta-grid">
                    <div className="cc-meta-row">
                      <span className="cc-meta-label">Duration</span>
                      <span className="cc-meta-value">{selectedCourse.duration}</span>
                    </div>
                    {selectedCourse.isExam && selectedCourse.attemptsText && (
                      <div className="cc-meta-row">
                        <span className="cc-meta-label">Exam Voucher</span>
                        <span className="cc-meta-value">{selectedCourse.attemptsText}</span>
                      </div>
                    )}
                    {selectedCourse.isTraining && selectedCourse.dbCourse?.Type?.toLowerCase().includes('exam') && selectedCourse.attemptsText && (
                      <div className="cc-meta-row">
                        <span className="cc-meta-label">Exam Voucher</span>
                        <span className="cc-meta-value">{selectedCourse.attemptsText}</span>
                      </div>
                    )}
                    <div className="cc-meta-row">
                      <span className="cc-meta-label">Venue</span>
                      <span className="cc-meta-value">{selectedCourse.venue}</span>
                    </div>
                    <div className="cc-meta-row">
                      <span className="cc-meta-label">Trainer</span>
                      <span className="cc-meta-value">{selectedCourse.trainer}</span>
                    </div>
                    <div className="cc-meta-row">
                      <span className="cc-meta-label">Category</span>
                      <span className="cc-meta-value">{selectedCourse.category}</span>
                    </div>
                    <div className="cc-meta-row">
                      <span className="cc-meta-label">Fee</span>
                      <span className="cc-meta-value cc-meta-fee">{selectedCourse.fee}</span>
                    </div>
                  </div>
                </div>

                {/* Description card */}
                <div className="cc-info-card">
                  <h3 className="cc-info-card-title">About this training</h3>
                  <div
                    className="cc-info-card-body"
                    dangerouslySetInnerHTML={{ __html: selectedCourse.description || 'No course description available.' }}
                  />

                  <h3 className="cc-info-card-title" style={{ marginTop: '1.5rem' }}>Course Overview</h3>
                  <p className="cc-info-card-body">
                    This training includes complete materials, training guides, exercises, and official reference documents to prepare learners for success in their professional development.
                  </p>
                </div>
              </div>

              {/* ── Right: Mini-Calendar + Registration ── */}
              {user?.role !== 'Admin' && (
                <div className="cc-detail-right">
                  <div className="cc-register-card">
                    <div className="cc-register-card-header">
                      <h3>Register for this training</h3>
                      <p>Select any date of your preferred session below.</p>
                    </div>

                    {/* Active registrations for this course */}
                    {(() => {
                      const courseRegs = registrations.filter(r =>
                        selectedCourse.sessions.some(s => s.id === `course-${r.ScheduleID}`)
                      );
                      if (courseRegs.length === 0) return null;
                      return (
                        <div className="cc-booked-sessions-section" style={{ marginBottom: '1.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem' }}>
                          <h4 style={{ margin: '0 0 0.75rem 0', color: '#1e3a5f', fontWeight: '800', fontSize: '0.95rem' }}>
                            Your Current Bookings
                          </h4>
                          {courseRegs.map(reg => {
                            const matchedSession = selectedCourse.sessions.find(s => s.id === `course-${reg.ScheduleID}`);
                            const displayStartDate = matchedSession ? matchedSession.startDate : reg.StartDate;
                            const displayEndDate = matchedSession ? matchedSession.endDate : reg.EndDate;
                            const displayStartTime = matchedSession ? matchedSession.startTime : reg.StartTime;
                            const displayEndTime = matchedSession ? matchedSession.endTime : reg.EndTime;

                            let statusColor = '#d97706';
                            let statusBg = '#fffbeb';
                            if (reg.Status === 'Approved') {
                              statusColor = '#15803d';
                              statusBg = '#dcfce7';
                            } else if (reg.Status === 'Pending Payment') {
                              statusColor = '#0369a1';
                              statusBg = '#e0f2fe';
                            } else if (reg.Status === 'Rejected' || reg.Status === 'Cancelled') {
                              statusColor = '#ef4444';
                              statusBg = '#fef2f2';
                            } else if (reg.Status === 'Withdrawn') {
                              statusColor = '#64748b';
                              statusBg = '#f1f5f9';
                            }

                            const canWithdraw = reg.Status !== 'Cancelled' && reg.Status !== 'Withdrawn' && reg.Status !== 'Rejected';

                            return (
                              <div key={reg.RegistrationID} className="cc-booked-session-card" style={{ marginBottom: '0.75rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold' }}>
                                    ID: {reg.RegistrationID}
                                  </span>
                                  <span className="status-badge" style={{
                                    fontSize: '10px',
                                    padding: '0.2rem 0.55rem',
                                    fontWeight: '700',
                                    borderRadius: '6px',
                                    color: statusColor,
                                    background: statusBg
                                  }}>
                                    {reg.Status}
                                  </span>
                                </div>
                                <div style={{ fontSize: '13px', fontWeight: '800', color: '#1e293b', marginBottom: '0.25rem' }}>
                                  {formatToDMY(displayStartDate)} – {formatToDMY(displayEndDate)}
                                </div>
                                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', marginBottom: canWithdraw ? '0.85rem' : '0' }}>
                                  Time: {displayStartTime.toLowerCase()} – {displayEndTime.toLowerCase()}
                                </div>

                                {canWithdraw && (
                                  <button
                                    className="cc-withdraw-btn"
                                    onClick={() => handleWithdraw(reg.RegistrationID)}
                                  >
                                    Withdraw Registration
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Mini-calendar */}
                    <div className="cc-mini-cal-section">
                      <div className="cc-mini-cal-label">Select Session Date</div>
                      <div className="cc-mini-cal">
                        <div className="cc-mini-cal-nav">
                          <button className="cc-mini-nav-btn" onClick={prevDetailMonth} style={{ fontWeight: 'bold' }}>
                            &lt;
                          </button>
                          <span className="cc-mini-cal-month">
                            {MONTH_NAMES[detailMonth]} {detailYear}
                          </span>
                          <button className="cc-mini-nav-btn" onClick={nextDetailMonth} style={{ fontWeight: 'bold' }}>
                            &gt;
                          </button>
                        </div>
                        <div className="cc-mini-day-headers">
                          {DAY_NAMES.map(d => <span key={d}>{d}</span>)}
                        </div>
                        <div className="cc-mini-grid">
                          {Array.from({ length: detailFirstDay }).map((_, i) => (
                            <div key={`e${i}`} className="cc-mini-cell cc-mini-cell-empty" />
                          ))}
                          {Array.from({ length: detailDaysInMonth }).map((_, i) => {
                            const day = i + 1;
                            const dateStr = toDateStr(detailYear, detailMonth, day);
                            const sessionForDate = courseDateMap[dateStr];
                            const isAvailable = !!sessionForDate;

                            // Range selection - check if this day is part of the selected session
                            const isSelected = selectedSession && selectedSession.dates.includes(dateStr);
                            const isToday = day === today.getDate() && detailMonth === today.getMonth() && detailYear === today.getFullYear();

                            return (
                              <button
                                key={day}
                                className={`cc-mini-cell ${isAvailable ? 'cc-mini-cell-available' : 'cc-mini-cell-unavail'} ${isSelected ? 'cc-mini-cell-selected' : ''} ${isToday && !isSelected ? 'cc-mini-cell-today' : ''}`}
                                style={isSelected ? { background: selectedCourse.accentColor, color: '#fff', borderColor: selectedCourse.accentColor } : isAvailable ? { borderColor: selectedCourse.accentColor, color: selectedCourse.accentColor } : {}}
                                disabled={!isAvailable}
                                onClick={() => {
                                  setSelectedSession(sessionForDate);
                                  setSelectedTimeSlot(`${sessionForDate.startTime} – ${sessionForDate.endTime}`);
                                  if (sessionForDate.isCancelled) {
                                    setClashError('Registration for this course session has been cancelled by the admin.');
                                  } else if (sessionForDate.isClosed) {
                                    setClashError('Registration for this course session has been closed by the admin.');
                                  } else if (sessionForDate.isFull) {
                                    setClashError(`This session has reached its maximum capacity limit of ${sessionForDate.maxCapacity} participants.`);
                                  } else {
                                    const clashingCourse = checkClash(sessionForDate);
                                    if (clashingCourse) {
                                      if (clashingCourse.includes("already registered")) {
                                        setClashError(clashingCourse);
                                      } else {
                                        setClashError(`Clashes with booked course: ${clashingCourse} (${formatToDMY(sessionForDate.startDate)} to ${formatToDMY(sessionForDate.endDate)}, ${sessionForDate.startTime.toLowerCase()} - ${sessionForDate.endTime.toLowerCase()}).`);
                                      }
                                    } else {
                                      setClashError(null);
                                      if (learnerBlackout?.isBlacklisted) {
                                        setClashError(learnerBlackout.blackout?.Reason || 'Your account is currently under enrollment suspension (blackout period).');
                                      } else {
                                        evaluateEligibility(sessionForDate);
                                      }
                                    }
                                  }
                                }}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                        <div className="cc-mini-legend">
                          <span className="cc-mini-legend-dot" style={{ background: selectedCourse.accentColor }} />
                          <span>Available session date</span>
                        </div>
                      </div>
                    </div>

                    {/* Connected Time period section */}
                    {selectedSession && (
                      <div className="cc-timeslot-section">
                        {selectedSession.sessionSegments && selectedSession.sessionSegments.length > 1 ? (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                              <div className="cc-timeslot-label" style={{ margin: 0 }}>Time period (Split Session)</div>
                              <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#047857', background: '#ecfdf5', padding: '2px 8px', borderRadius: '10px', border: '1px solid #a7f3d0' }}>
                                {selectedSession.sessionSegments.length} Parts ({selectedSession.dates?.length || 0} Total Days)
                              </span>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '0.5rem' }}>
                              {selectedSession.sessionSegments.map((seg, idx) => (
                                <div key={idx} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.65rem 0.75rem' }}>
                                  <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#047857', marginBottom: '0.35rem' }}>
                                    Part {idx + 1}
                                  </div>
                                  <div className="cc-time-period-container" style={{ gap: '0.35rem' }}>
                                    <div className="cc-time-period-row">
                                      <span className="cc-time-period-label">From</span>
                                      <span className="cc-time-period-box">{formatToDMY(seg.startDate)}</span>
                                      <span className="cc-time-period-box">{(seg.startTime || selectedSession.startTime).toLowerCase()}</span>
                                    </div>
                                    <div className="cc-time-period-row" style={{ marginTop: '0.35rem' }}>
                                      <span className="cc-time-period-label">Till</span>
                                      <span className="cc-time-period-box">{formatToDMY(seg.endDate)}</span>
                                      <span className="cc-time-period-box">{(seg.endTime || selectedSession.endTime).toLowerCase()}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="cc-timeslot-label">Time period</div>
                            <div className="cc-time-period-container">
                              <div className="cc-time-period-row">
                                <span className="cc-time-period-label">From</span>
                                <span className="cc-time-period-box">{formatToDMY(selectedSession.startDate)}</span>
                                <span className="cc-time-period-box">{selectedSession.startTime.toLowerCase()}</span>
                              </div>
                              <div className="cc-time-period-row" style={{ marginTop: '0.5rem' }}>
                                <span className="cc-time-period-label">Till</span>
                                <span className="cc-time-period-box">{formatToDMY(selectedSession.endDate)}</span>
                                <span className="cc-time-period-box">{selectedSession.endTime.toLowerCase()}</span>
                              </div>
                            </div>
                          </>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem', padding: '0.55rem 0.85rem', background: selectedSession.isCancelled || selectedSession.isClosed || selectedSession.isFull ? '#fef2f2' : '#f0fdf4', borderRadius: '8px', border: `1px solid ${selectedSession.isCancelled || selectedSession.isClosed || selectedSession.isFull ? '#fecaca' : '#bbf7d0'}`, fontSize: '0.825rem' }}>
                          <span style={{ fontWeight: '600', color: '#475569' }}>Session Seats:</span>
                          <span style={{ fontWeight: '800', color: selectedSession.isCancelled || selectedSession.isClosed || selectedSession.isFull ? '#dc2626' : '#15803d' }}>
                            {selectedSession.isCancelled ? '🚫 Cancelled by Admin' : selectedSession.isClosed ? '🔒 Closed by Admin' : selectedSession.isFull ? `FULL (${selectedSession.enrolledCount}/${selectedSession.maxCapacity})` : `${selectedSession.enrolledCount} / ${selectedSession.maxCapacity} Enrolled`}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Active Blackout Banner */}
                    {learnerBlackout?.isBlacklisted && (
                      <div style={{
                        marginTop: '1rem',
                        marginBottom: '0.75rem',
                        padding: '1rem 1.15rem',
                        borderRadius: '10px',
                        background: '#fef2f2',
                        border: '1.5px solid #fca5a5',
                        color: '#991b1b',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px'
                      }}>
                        <span style={{ fontSize: '1.35rem', lineHeight: 1 }}>🛑</span>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: '800', fontSize: '0.9rem' }}>
                            Enrollment Blackout Policy in Effect
                          </div>
                          <p style={{ margin: '4px 0 0 0', fontSize: '0.825rem', lineHeight: 1.45, color: '#b91c1c' }}>
                            {learnerBlackout.blackout?.Reason || 'Your account has been suspended from enrolling in training courses due to repeat poor attendance or no-shows.'}
                          </p>
                          <div style={{ marginTop: '6px', fontSize: '0.8rem', fontWeight: '700', color: '#dc2626' }}>
                            Suspension Window: {learnerBlackout.daysRemaining} days remaining (Ends {learnerBlackout.blackout?.EndDate ? learnerBlackout.blackout.EndDate.split(' ')[0] : 'in 2 months'})
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Re-Enrollment Policy Notice */}
                    {eligibilityData?.isReEnrollment && !learnerBlackout?.isBlacklisted && (
                      <div style={{
                        marginTop: '1rem',
                        marginBottom: '0.75rem',
                        padding: '0.85rem 1rem',
                        borderRadius: '8px',
                        background: '#fffbeb',
                        border: '1px solid #fde68a',
                        color: '#92400e',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px'
                      }}>
                        <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>⚠️</span>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: '700', fontSize: '0.85rem' }}>
                            Re-Enrollment Policy Notice
                          </div>
                          <p style={{ margin: '3px 0 0 0', fontSize: '0.8rem', lineHeight: 1.4, color: '#b45309' }}>
                            {eligibilityData.message}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Register button */}
                    <button
                      className="cc-register-btn"
                      style={selectedSession && selectedTimeSlot && !clashError && !learnerBlackout?.isBlacklisted ? { background: selectedCourse.accentColor } : {}}
                      disabled={!selectedSession || !selectedTimeSlot || registering || !!clashError || Boolean(learnerBlackout?.isBlacklisted)}
                      onClick={handleRegister}
                    >
                      {registering ? (
                        <span className="cc-register-btn-loading">
                          Registering...
                        </span>
                      ) : learnerBlackout?.isBlacklisted ? 'Enrollment Suspended (Blackout)' : 'Register'}
                    </button>

                    {clashError && (
                      <div className="cc-clash-error-banner" style={{
                        marginTop: '1rem',
                        padding: '0.85rem 1rem',
                        borderRadius: '8px',
                        background: '#fef2f2',
                        border: '1px solid #fee2e2',
                        color: '#991b1b',
                        fontSize: '0.85rem',
                        lineHeight: '1.4',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '1.1rem', marginTop: '-2px' }}>⚠️</span>
                        <span>{clashError}</span>
                      </div>
                    )}

                    {!selectedSession && (
                      <p className="cc-register-hint">Please select an available session date to continue.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* ── SUCCESS DIALOG ── */}
      {showSuccess && selectedCourse && selectedSession && (
        <div className="cc-success-overlay" onClick={handleSuccessDone}>
          <div className="cc-success-modal" onClick={e => e.stopPropagation()}>
            <div style={{ background: '#fef3c7', color: '#d97706', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
              <SvgIcon name="clock" size={28} />
            </div>
            <h2 className="cc-success-title">Registration Pending Approval</h2>
            <p className="cc-success-subtitle">Your registration request has been submitted and is pending admin approval:</p>
            {successNotice && (
              <div style={{
                margin: '0.75rem 0',
                padding: '0.65rem 0.85rem',
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '8px',
                color: '#92400e',
                fontSize: '0.825rem',
                textAlign: 'left',
                lineHeight: 1.45
              }}>
                <strong>⚠️ Policy Notice:</strong> {successNotice}
              </div>
            )}
            <div className="cc-success-details">
              <div className="cc-success-detail-row">
                <span className="cc-success-detail-label">Course</span>
                <span className="cc-success-detail-value">{selectedCourse.name}</span>
              </div>
              {selectedSession.sessionSegments && selectedSession.sessionSegments.length > 1 ? (
                <div className="cc-success-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                  <span className="cc-success-detail-label">Training Schedule Breakdown</span>
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '2px' }}>
                    {selectedSession.sessionSegments.map((s, idx) => (
                      <div key={idx} style={{ fontSize: '0.825rem', color: '#1e293b', background: '#f8fafc', padding: '5px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                        <strong style={{ color: '#047857' }}>Part {idx + 1}:</strong>
                        <span>{formatDisplayDate(s.startDate)} – {formatDisplayDate(s.endDate)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="cc-success-detail-row">
                    <span className="cc-success-detail-label">Start Date</span>
                    <span className="cc-success-detail-value">{formatDisplayDate(selectedSession.startDate)}</span>
                  </div>
                  <div className="cc-success-detail-row">
                    <span className="cc-success-detail-label">End Date</span>
                    <span className="cc-success-detail-value">{formatDisplayDate(selectedSession.endDate)}</span>
                  </div>
                </>
              )}
              <div className="cc-success-detail-row">
                <span className="cc-success-detail-label">Time</span>
                <span className="cc-success-detail-value">{selectedTimeSlot}</span>
              </div>
              <div className="cc-success-detail-row">
                <span className="cc-success-detail-label">Venue</span>
                <span className="cc-success-detail-value">{selectedCourse.venue}</span>
              </div>
              <div className="cc-success-detail-row">
                <span className="cc-success-detail-label">Trainer</span>
                <span className="cc-success-detail-value">{selectedCourse.trainer}</span>
              </div>
            </div>
            <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ color: '#059669', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <SvgIcon name="mail" size={18} />
              </div>
              <span style={{ fontSize: '0.85rem', color: '#065f46', lineHeight: '1.4', textAlign: 'left' }}>
                A confirmation email with your enrollment details has been sent to <strong>{user?.email}</strong>.
              </span>
            </div>
            <p className="cc-success-note">
              Admins review all registrations within 3 to 5 working days. You will receive another email notification once your request has been approved.
            </p>
            <button className="cc-success-done-btn" style={{ background: '#d97706' }} onClick={handleSuccessDone}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* ── WITHDRAW CONFIRM DIALOG ── */}
      {showWithdrawConfirm && (
        <div className="cc-success-overlay" onClick={() => setShowWithdrawConfirm(null)}>
          <div className="cc-success-modal" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#fee2e2', color: '#ef4444', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '2rem', fontWeight: 'bold' }}>
              ⚠️
            </div>
            <h2 className="cc-success-title" style={{ fontSize: '1.25rem' }}>Withdraw from Session?</h2>
            <p className="cc-success-subtitle" style={{ fontSize: '0.875rem', marginBottom: '1.5rem', color: '#64748b', lineHeight: '1.4' }}>
              Are you sure you want to withdraw from this training session? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                className="cc-success-done-btn"
                style={{ background: '#ef4444', margin: '0', flex: 1, padding: '0.75rem' }}
                onClick={() => {
                  const regId = showWithdrawConfirm;
                  setShowWithdrawConfirm(null);
                  executeWithdraw(regId);
                }}
              >
                Yes, Withdraw
              </button>
              <button
                className="cc-success-done-btn"
                style={{ background: '#cbd5e1', color: '#1e293b', margin: '0', flex: 1, padding: '0.75rem' }}
                onClick={() => setShowWithdrawConfirm(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── WITHDRAW SUCCESS DIALOG ── */}
      {showWithdrawSuccess && (
        <div className="cc-success-overlay" onClick={() => setShowWithdrawSuccess(false)}>
          <div className="cc-success-modal" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#dcfce7', color: '#15803d', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '2rem', fontWeight: 'bold' }}>
              ✓
            </div>
            <h2 className="cc-success-title">Withdrawal Successful</h2>
            <p className="cc-success-subtitle" style={{ color: '#64748b', marginBottom: '1.5rem' }}>
              You have successfully withdrawn from the training session.
            </p>
            <button className="cc-success-done-btn" style={{ background: '#16a34a', margin: '0' }} onClick={() => setShowWithdrawSuccess(false)}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* ── LEARNER SELF CHECK-IN MODAL ── */}
      {selfCheckInOpen && (
        <div className="td-modal-overlay" onClick={() => setSelfCheckInOpen(false)}>
          <div className="td-modal-content" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <button className="td-modal-close" onClick={() => setSelfCheckInOpen(false)}>
              ✕
            </button>
            <div className="td-modal-header" style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
              <div style={{ display: 'inline-flex', padding: '12px', background: '#dcfce7', color: '#15803d', borderRadius: '50%', marginBottom: '10px' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <h2 style={{ fontSize: '1.4rem', margin: '0 0 0.4rem 0' }}>Class Self Check-In</h2>
              <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
                Enter the 6-character Session Code generated by your trainer in class to check in.
              </p>
            </div>

            <form onSubmit={handleSelfCheckInSubmit} style={{ padding: '0 1.5rem 1.5rem' }}>
              {selfCheckInMsg && (
                <div style={{
                  padding: '0.85rem 1rem',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  background: selfCheckInMsg.type === 'success' ? '#dcfce7' : '#fef2f2',
                  color: selfCheckInMsg.type === 'success' ? '#15803d' : '#b91c1c',
                  border: `1px solid ${selfCheckInMsg.type === 'success' ? '#bbf7d0' : '#fecaca'}`
                }}>
                  {selfCheckInMsg.text}
                </div>
              )}

              <div className="td-form-group" style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem' }}>
                  Session Code from Trainer:
                </label>
                <input
                  type="text"
                  value={selfCheckInCode}
                  onChange={e => setSelfCheckInCode(e.target.value.toUpperCase())}
                  placeholder="e.g., 8F3A2B"
                  maxLength={10}
                  required
                  style={{
                    width: '100%',
                    padding: '0.8rem 1rem',
                    fontSize: '1.25rem',
                    fontWeight: '800',
                    textAlign: 'center',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    border: '2px solid #cbd5e1',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={selfCheckInSubmitting}
                style={{
                  width: '100%',
                  padding: '0.8rem',
                  background: '#4f46e5',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                {selfCheckInSubmitting ? 'Verifying...' : 'Submit Check-In'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Special-Project Access Denied Modal */}
      {accessDeniedModal?.isOpen && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '1rem'
        }} data-testid="access-denied-modal">
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            maxWidth: '480px',
            width: '100%',
            padding: '2rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                backgroundColor: '#fee2e2',
                color: '#ef4444',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <SvgIcon name="lock" size={22} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a', fontWeight: 800 }}>
                  Special-Project Access Restricted
                </h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '0.825rem', color: '#64748b' }}>
                  Authorization required to view or book this course
                </p>
              </div>
            </div>

            <p style={{ margin: 0, fontSize: '0.9rem', color: '#334155', lineHeight: 1.5 }}>
              <strong>{accessDeniedModal.courseName || 'This course'}</strong> is restricted to learners enrolled in designated special-project cohorts. Please enter your project authorization code to unlock access.
            </p>

            <form onSubmit={handleUnlockCourse} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <input
                  type="text"
                  placeholder="E.G. CYBER-GOV-2026"
                  value={modalProjectCode}
                  onChange={(e) => setModalProjectCode(e.target.value.toUpperCase())}
                  data-testid="access-denied-code-input"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                {accessDeniedModal.error && (
                  <p style={{ margin: '4px 0 0 0', color: '#ef4444', fontSize: '0.8rem', fontWeight: 600 }} data-testid="unlock-error-msg">
                    {accessDeniedModal.error}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setAccessDeniedModal(null)}
                  data-testid="cancel-unlock-btn"
                  style={{
                    padding: '0.6rem 1.1rem',
                    backgroundColor: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={accessDeniedModal.loading || !modalProjectCode.trim()}
                  data-testid="unlock-course-btn"
                  style={{
                    padding: '0.6rem 1.25rem',
                    backgroundColor: '#4f46e5',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  {accessDeniedModal.loading ? 'Unlocking...' : 'Unlock Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── BLACKOUT POLICY SUSPENSION MODAL ── */}
      {blackoutAlertModal && blackoutAlertModal.isOpen && (
        <div className="cc-success-overlay" onClick={() => setBlackoutAlertModal(null)}>
          <div className="cc-success-modal" style={{ maxWidth: '480px', borderTop: '4px solid #ef4444' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#fee2e2', color: '#dc2626', width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
              <SvgIcon name="slash" size={32} />
            </div>
            <h2 className="cc-success-title" style={{ color: '#991b1b', fontSize: '1.35rem' }}>Enrollment Blackout Policy</h2>
            <p className="cc-success-subtitle" style={{ color: '#7f1d1d', lineHeight: '1.5' }}>
              {blackoutAlertModal.message}
            </p>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.85rem 1rem', margin: '1rem 0', textAlign: 'left', fontSize: '0.825rem' }}>
              <div style={{ color: '#475569', marginBottom: '4px' }}>
                <strong>Reason:</strong> {blackoutAlertModal.reason || '3 repeat registrations with incomplete or zero attendance'}
              </div>
              <div style={{ color: '#475569' }}>
                <strong>Suspension Ends:</strong> {blackoutAlertModal.endDate ? blackoutAlertModal.endDate.split('T')[0] : 'In 2 months'} ({blackoutAlertModal.daysRemaining} days remaining)
              </div>
            </div>

            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.75rem 0 1.25rem', lineHeight: '1.4' }}>
              If you believe this suspension was triggered in error or have extenuating circumstances, please contact the program administrator to request an enrollment override.
            </p>

            <button
              className="cc-success-btn"
              style={{ background: '#0f172a' }}
              onClick={() => setBlackoutAlertModal(null)}
            >
              Understood
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseCalendar;
